import React, { useState, useEffect } from 'react';
import { useStudioStore } from '@services/state/studioStore';

export const OverlayControls: React.FC = () => {
  const clock = useStudioStore((state) => state.clock);
  const liveIndicator = useStudioStore((state) => state.liveIndicator);
  const setClock = useStudioStore((state) => state.setClock);
  const setLiveIndicator = useStudioStore((state) => state.setLiveIndicator);
  const toggleClock = useStudioStore((state) => state.toggleClock);
  const toggleLiveIndicator = useStudioStore((state) => state.toggleLiveIndicator);

  // State for actual stage dimensions
  const [stageDimensions, setStageDimensions] = useState({
    width: 1920,
    height: 1080
  });

  // Update stage dimensions by observing the actual .studio-stage element
  useEffect(() => {
    const updateStageDimensions = () => {
      const stageElement = document.querySelector('.studio-stage') as HTMLElement;
      if (stageElement) {
        const rect = stageElement.getBoundingClientRect();
        setStageDimensions({
          width: rect.width,
          height: rect.height
        });
      }
    };

    // Update dimensions on mount and when window resizes
    updateStageDimensions();
    window.addEventListener('resize', updateStageDimensions);

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateStageDimensions);
    };
  }, []);

  // Calculate positioning limits accounting for element size
  const getPositioningLimits = () => {
    // Estimate element dimensions for positioning limits
    let clockElementWidth = 0;
    let clockElementHeight = 0;
    
    // Calculate width based on what's being displayed
    if (clock.showDate && clock.showTime) {
      // Both date and time - use the wider of the two
      clockElementWidth = clock.style.fontSize * (clock.dateFormat === 'long' ? 6 : 4);
    } else if (clock.showDate) {
      // Date only
      clockElementWidth = clock.style.fontSize * (clock.dateFormat === 'long' ? 6 : 4);
    } else if (clock.showTime) {
      // Time only
      clockElementWidth = clock.style.fontSize * 4; // Approx 4 characters wide (HH:MM)
    }
    
    // Calculate height based on what's being displayed
    if (clock.showDate && clock.showTime) {
      // Both date and time
      clockElementHeight = (clock.style.fontSize * 1.2) + (clock.style.fontSize * 0.8 * 1.2) + 4; // Time + date + margin
    } else if (clock.showDate || clock.showTime) {
      // Either date only or time only
      clockElementHeight = clock.style.fontSize * 1.2;
    }
    
    const liveIndicatorWidth = 80; // Approximate width of "LIVE" indicator
    const liveIndicatorHeight = 30; // Approximate height including padding
    
    return {
      clock: {
        maxX: Math.max(0, stageDimensions.width - clockElementWidth),
        maxY: Math.max(0, stageDimensions.height - clockElementHeight)
      },
      liveIndicator: {
        maxX: Math.max(0, stageDimensions.width - liveIndicatorWidth),
        maxY: Math.max(0, stageDimensions.height - liveIndicatorHeight)
      }
    };
  };

  const limits = getPositioningLimits();

  return (
    <div className="control-section">
      <h3>Date/Time & Indicators</h3>
      
      {/* Date/Time Controls */}
      <div className="control-group">
        <h4 style={{ margin: '0 0 12px 0', color: '#fff', fontSize: '14px' }}>Date/Time</h4>
        
        <div className="control-checkbox">
          <input
            type="checkbox"
            id="clockVisible"
            checked={clock.visible}
            onChange={toggleClock}
          />
          <label htmlFor="clockVisible">Show Date/Time Overlay</label>
        </div>

        {clock.visible && (
          <>
            <div className="control-checkbox">
              <input
                type="checkbox"
                id="showDate"
                checked={clock.showDate}
                onChange={(e) => setClock({ showDate: e.target.checked })}
              />
              <label htmlFor="showDate">Show Date</label>
            </div>

            <div className="control-checkbox">
              <input
                type="checkbox"
                id="showTime"
                checked={clock.showTime}
                onChange={(e) => setClock({ showTime: e.target.checked })}
              />
              <label htmlFor="showTime">Show Time</label>
            </div>
          </>
        )}

        {clock.visible && clock.showDate && (
          <div style={{ marginBottom: '12px' }}>
            <label className="control-label">Date Format</label>
            <select
              className="control-select"
              value={clock.dateFormat}
              onChange={(e) => setClock({ dateFormat: e.target.value as 'short' | 'medium' | 'long' })}
            >
              <option value="short">Short (12/25/24)</option>
              <option value="medium">Medium (Wed, Dec 25)</option>
              <option value="long">Long (Wednesday, December 25, 2024)</option>
            </select>
          </div>
        )}

        {clock.visible && clock.showTime && (
          <>
            <div style={{ marginBottom: '12px' }}>
              <label className="control-label">Time Format</label>
              <select
                className="control-select"
                value={clock.format}
                onChange={(e) => setClock({ format: e.target.value as '12h' | '24h' })}
              >
                <option value="12h">12 Hour (AM/PM)</option>
                <option value="24h">24 Hour</option>
              </select>
            </div>

            <div className="control-checkbox">
              <input
                type="checkbox"
                id="showSeconds"
                checked={clock.showSeconds}
                onChange={(e) => setClock({ showSeconds: e.target.checked })}
              />
              <label htmlFor="showSeconds">Show Seconds</label>
            </div>
          </>
        )}

        {clock.visible && (clock.showDate || clock.showTime) && (
          <>
            <div style={{ marginBottom: '12px' }}>
              <label className="control-label">Timezone</label>
              <select
                className="control-select"
                value={clock.timezone}
                onChange={(e) => setClock({ timezone: e.target.value })}
              >
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
                <option value="UTC">UTC</option>
                <option value="Europe/London">London</option>
                <option value="Europe/Paris">Paris</option>
                <option value="Asia/Tokyo">Tokyo</option>
                <option value="Australia/Sydney">Sydney</option>
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label className="control-label">Position X: {clock.position.x}px</label>
              <input
                type="range"
                className="control-input"
                min="0"
                max={limits.clock.maxX}
                value={Math.min(clock.position.x, limits.clock.maxX)}
                onChange={(e) => setClock({ 
                  position: { ...clock.position, x: Number(e.target.value) }
                })}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label className="control-label">Position Y: {clock.position.y}px</label>
              <input
                type="range"
                className="control-input"
                min="0"
                max={limits.clock.maxY}
                value={Math.min(clock.position.y, limits.clock.maxY)}
                onChange={(e) => setClock({ 
                  position: { ...clock.position, y: Number(e.target.value) }
                })}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label className="control-label">Text Color</label>
              <div className="color-picker">
                <input
                  type="color"
                  value={clock.style.color}
                  onChange={(e) => setClock({ 
                    style: { ...clock.style, color: e.target.value }
                  })}
                />
                <input
                  type="text"
                  className="control-input"
                  style={{ flex: 1 }}
                  value={clock.style.color}
                  onChange={(e) => setClock({ 
                    style: { ...clock.style, color: e.target.value }
                  })}
                />
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label className="control-label">Font Size: {clock.style.fontSize}px</label>
              <input
                type="range"
                className="control-input"
                min="16"
                max="48"
                value={clock.style.fontSize}
                onChange={(e) => setClock({ 
                  style: { ...clock.style, fontSize: Number(e.target.value) }
                })}
              />
            </div>
          </>
        )}
      </div>

      {/* Live Indicator Controls */}
      <div className="control-group" style={{ borderTop: '1px solid #333', paddingTop: '16px' }}>
        <h4 style={{ margin: '0 0 12px 0', color: '#fff', fontSize: '14px' }}>Live Indicator</h4>
        
        <div className="control-checkbox">
          <input
            type="checkbox"
            id="liveVisible"
            checked={liveIndicator.visible}
            onChange={toggleLiveIndicator}
          />
          <label htmlFor="liveVisible">Show Live Indicator</label>
        </div>

        {liveIndicator.visible && (
          <>
            <div style={{ marginBottom: '12px' }}>
              <label className="control-label">Text</label>
              <input
                type="text"
                className="control-input"
                value={liveIndicator.text}
                onChange={(e) => setLiveIndicator({ text: e.target.value })}
                placeholder="LIVE"
              />
            </div>

            <div className="control-checkbox">
              <input
                type="checkbox"
                id="blinking"
                checked={liveIndicator.blinking}
                onChange={(e) => setLiveIndicator({ blinking: e.target.checked })}
              />
              <label htmlFor="blinking">Blinking Animation</label>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label className="control-label">Position X: {liveIndicator.position.x}px</label>
              <input
                type="range"
                className="control-input"
                min="0"
                max={limits.liveIndicator.maxX}
                value={Math.min(liveIndicator.position.x, limits.liveIndicator.maxX)}
                onChange={(e) => setLiveIndicator({ 
                  position: { ...liveIndicator.position, x: Number(e.target.value) }
                })}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label className="control-label">Position Y: {liveIndicator.position.y}px</label>
              <input
                type="range"
                className="control-input"
                min="0"
                max={limits.liveIndicator.maxY}
                value={Math.min(liveIndicator.position.y, limits.liveIndicator.maxY)}
                onChange={(e) => setLiveIndicator({ 
                  position: { ...liveIndicator.position, y: Number(e.target.value) }
                })}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label className="control-label">Color</label>
              <div className="color-picker">
                <input
                  type="color"
                  value={liveIndicator.color}
                  onChange={(e) => setLiveIndicator({ color: e.target.value })}
                />
                <input
                  type="text"
                  className="control-input"
                  style={{ flex: 1 }}
                  value={liveIndicator.color}
                  onChange={(e) => setLiveIndicator({ color: e.target.value })}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Quick Presets */}
      <div className="control-group" style={{ borderTop: '1px solid #333', paddingTop: '16px' }}>
        <h4 style={{ margin: '0 0 8px 0', color: '#ccc', fontSize: '12px' }}>Quick Layouts</h4>
        <div style={{ display: 'grid', gap: '8px' }}>
          <button
            className="control-button"
            onClick={() => {
              setClock({ 
                visible: true,
                showTime: true,
                showDate: true,
                dateFormat: 'medium',
                position: { x: 20, y: 20 },
                style: { ...clock.style, fontSize: 24 }
              });
              setLiveIndicator({ 
                visible: true,
                position: { x: 20, y: 80 }, // Adjusted for date+time display
                color: '#ff0000'
              });
            }}
          >
            Top Left (Date & Time)
          </button>
          <button
            className="control-button"
            onClick={() => {
              setClock({ 
                visible: true,
                showTime: false,
                showDate: true,
                dateFormat: 'long',
                position: { x: Math.max(0, stageDimensions.width - 250), y: 20 },
                style: { ...clock.style, fontSize: 20 }
              });
              setLiveIndicator({ 
                visible: true,
                position: { x: Math.max(0, stageDimensions.width - 200), y: 50 },
                color: '#00ff00'
              });
            }}
          >
            Top Right (Date Only)
          </button>
          <button
            className="control-button"
            onClick={() => {
              setClock({ 
                visible: true,
                showTime: true,
                showDate: false,
                position: { x: Math.max(0, stageDimensions.width - 150), y: 20 },
                style: { ...clock.style, fontSize: 20 }
              });
              setLiveIndicator({ 
                visible: true,
                position: { x: Math.max(0, stageDimensions.width - 150), y: 50 },
                color: '#0066ff'
              });
            }}
          >
            Top Right (Time Only)
          </button>
          <button
            className="control-button"
            onClick={() => {
              setClock({ visible: false });
              setLiveIndicator({ visible: false });
            }}
          >
            Hide All
          </button>
        </div>
      </div>
    </div>
  );
};